import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { extend } from 'underscore';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, } from '@cargomatic/ui';
import Store from 'stores/TariffsStore';
import { addTariff, updateTariff } from 'utils/TariffAPIUtils';
import { ConfirmationModal } from 'components/v2/confirmation-modal';
import { TariffEditor } from './TariffEditor';
export function TariffEditorDialog({ tariff, tariffsList, onClose }) {
    const { t } = useTranslation();
    const draftRef = useRef(null);
    const [hasBeenEdited, setHasBeenEdited] = useState(false);
    const [valid, setValid] = useState(!!tariff);
    const [confirming, setConfirming] = useState(false);
    // Seed the draft from props on first render. Mirrors legacy class behavior:
    // a fresh copy so child edits don't mutate the parent's tariff object.
    if (draftRef.current === null) {
        draftRef.current = tariff ? extend({}, tariff) : null;
    }
    const newTariffMode = !draftRef.current || !draftRef.current._id;
    // Auto-close once the store reports a successful save/update.
    useEffect(() => {
        const onChange = () => {
            if (Store.getStatus() === 'success') {
                setHasBeenEdited(false);
                setValid(false);
                setConfirming(false);
                onClose();
            }
        };
        Store.addChangeListener(onChange);
        return () => Store.removeChangeListener(onChange);
    }, [onClose]);
    const handleEditorChange = useCallback((updated, edited, validated) => {
        draftRef.current = updated;
        setHasBeenEdited(edited);
        setValid(validated);
    }, []);
    const handleSaveNew = useCallback(() => {
        if (!draftRef.current)
            return;
        Store.reset();
        addTariff(draftRef.current);
    }, []);
    const handleConfirmUpdate = useCallback(() => {
        if (!draftRef.current)
            return;
        Store.reset();
        updateTariff(draftRef.current, '');
        setConfirming(false);
    }, []);
    const handleSaveClick = useCallback(() => {
        if (newTariffMode) {
            handleSaveNew();
        }
        else {
            setConfirming(true);
        }
    }, [newTariffMode, handleSaveNew]);
    const disableSaveButton = (!hasBeenEdited && !newTariffMode) || !valid;
    return (React.createElement(React.Fragment, null,
        React.createElement(Dialog, { open: true, onOpenChange: (open) => {
                if (!open)
                    onClose();
            } },
            React.createElement(DialogContent, { className: "sm:max-w-[900px] max-h-[85vh] overflow-y-auto tariff-wrapper" },
                React.createElement(DialogHeader, null,
                    React.createElement(DialogTitle, null, newTariffMode ? 'New Tariff' : 'Edit Tariff')),
                React.createElement(TariffEditor, { tariff: draftRef.current, tariffsList: tariffsList, onChange: handleEditorChange }),
                React.createElement(DialogFooter, null,
                    React.createElement(Button, { variant: "outline", onClick: onClose }, titleize(t('CLOSE'))),
                    React.createElement(Button, { onClick: handleSaveClick, disabled: disableSaveButton }, newTariffMode ? titleize(t('SAVE')) : t('SAVE_CHANGES'))))),
        React.createElement(ConfirmationModal, { open: confirming, title: t('WARNING'), description: t('IMPACT_CUSTOMERS_MSG'), onHide: () => {
                Store.reset();
                setConfirming(false);
            }, onConfirm: handleConfirmUpdate, confirmText: t('CONFIRM_UPDATE'), cancelText: titleize(t('CANCEL')) })));
}
export default TariffEditorDialog;
